// src/App.tsx
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import Routes from "./routes/Routes";
import { useState, useEffect } from "react";
import { FirebaseProvider } from "./providers/FirebaseProvider";
import { NativeFeatures } from "./services/nativeFeatures";
import ErrorBoundary from "./components/ErrorBoundary";
import PWAManager from "./components/pwa/PWAManager";

const App = () => {
  // Create a new QueryClient instance inside the component
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
      },
    }
  }));

  // Initialize native features on app startup
  useEffect(() => {
    const initializeApp = async () => {
      try {
        await NativeFeatures.initialize();
        
        // Set up app state change listener to sync data when app becomes active
        NativeFeatures.setAppStateChangeCallback((isActive) => {
          if (isActive) {
            console.log('App became active - triggering data sync');
            // The actual data sync will be handled by useStarData hook's visibility change listener
            // This ensures we don't duplicate the sync logic
            const event = new Event('visibilitychange');
            document.dispatchEvent(event);
          }
        });
        
        console.log('App initialization completed');
      } catch (error) {
        console.error('Error during app initialization:', error);
      }
    };

    initializeApp();
  }, []);

  // Set basename for GitHub Pages compatibility
  const basename =  '/';

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <FirebaseProvider>
          <TooltipProvider>
            <div className="h-screen w-full flex flex-col">
              <div className="flex-1 overflow-y-auto">
                <Toaster />
                <Sonner />
                <PWAManager />
                <BrowserRouter basename={basename}>
                  <Routes />
                </BrowserRouter>
              </div>
            </div>
          </TooltipProvider>
        </FirebaseProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;

